<template>
  <div class="w-full p-4">
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 px-4 pt-3 pb-1">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">All Expenses (Admin)</h2>
      <div class="text-xs text-gray-600">
        Read everything. Override status, reassign manager, delete, or restore from the row menu.
      </div>
    </div>
    <!-- Filter bar -->
    <div class="flex flex-wrap gap-3 mb-3 items-end text-sm">
      <div class="flex-1 min-w-[16rem]">
        <label class="block text-xs text-gray-500 mb-0.5">Search</label>
        <input
          v-model="filters.q"
          type="text"
          placeholder="Description, notes, group label, or #ID"
          class="w-full h-9 px-2 border border-gray-400 rounded-md"
        />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Status</label>
        <select v-model="filters.status" class="h-9 px-2 border border-gray-400 rounded-md w-56">
          <option :value="null">All</option>
          <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Submitter</label>
        <select v-model="filters.submitter" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="u in users" :key="u.id" :value="u.id">{{ pickerLabel(u) }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Manager</label>
        <select v-model="filters.manager" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="u in users" :key="u.id" :value="u.id">{{ pickerLabel(u) }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Created by (delegate)</label>
        <select v-model="filters.created_by" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="u in users" :key="u.id" :value="u.id">{{ pickerLabel(u) }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Category</label>
        <select v-model="filters.category" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Engagement</label>
        <select v-model="filters.engagement" class="h-9 px-2 border border-gray-400 rounded-md w-56">
          <option :value="null">All</option>
          <option v-for="e in engagements" :key="e.id" :value="e.id">
            {{ e.title || e.invoice_label || `Engagement #${e.id}` }}
          </option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Payment</label>
        <select v-model="filters.payment_method" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option value="personal_paid">Personal — Reimburse</option>
          <option value="company_paid">Company Card</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Client reimb.</label>
        <select v-model="filters.is_client_reimbursable" class="h-9 px-1 border border-gray-400 rounded-md w-20">
          <option :value="null">All</option>
          <option :value="true">Yes</option>
          <option :value="false">No</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Group (legacy)</label>
        <input
          v-model="filters.group_label__icontains"
          type="text"
          placeholder="Any"
          class="h-9 px-2 border border-gray-400 rounded-md w-32"
        />
      </div>
      <div class="min-w-[16rem]">
        <label class="block text-xs text-gray-500 mb-0.5 flex items-center gap-2">
          <span>Tags</span>
          <span v-if="filters.tags.length > 1" class="ml-auto inline-flex rounded border border-gray-300 overflow-hidden text-[10px]">
            <button type="button" :class="['px-1.5 py-0.5', filters.tag_combinator === 'or' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.tag_combinator = 'or'">OR</button>
            <button type="button" :class="['px-1.5 py-0.5 border-l border-gray-300', filters.tag_combinator === 'and' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.tag_combinator = 'and'">AND</button>
          </span>
        </label>
        <TagPicker v-model="filters.tags" placeholder="Any" />
      </div>
      <div class="min-w-[16rem]">
        <label class="block text-xs text-gray-500 mb-0.5 flex items-center gap-2">
          <span>Exclude Tags</span>
          <span v-if="filters.exclude_tags.length > 1" class="ml-auto inline-flex rounded border border-gray-300 overflow-hidden text-[10px]">
            <button type="button" :class="['px-1.5 py-0.5', filters.exclude_tag_combinator === 'or' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.exclude_tag_combinator = 'or'">OR</button>
            <button type="button" :class="['px-1.5 py-0.5 border-l border-gray-300', filters.exclude_tag_combinator === 'and' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.exclude_tag_combinator = 'and'">AND</button>
          </span>
        </label>
        <TagPicker v-model="filters.exclude_tags" placeholder="None" />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Date from</label>
        <input v-model="filters.date_of_expense__gte" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Date to</label>
        <input v-model="filters.date_of_expense__lte" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
      </div>
      <div class="flex gap-3">
        <div>
          <label class="block text-xs text-gray-500 mb-0.5">Reimb.</label>
          <select v-model="filters.submitter_reimbursed" class="h-9 px-1 border border-gray-400 rounded-md w-20">
            <option :value="null">All</option>
            <option :value="true">Yes</option>
            <option :value="false">No</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-0.5">Retro</label>
          <select v-model="filters.is_retro" class="h-9 px-1 border border-gray-400 rounded-md w-20">
            <option :value="null">All</option>
            <option :value="true">Yes</option>
            <option :value="false">No</option>
          </select>
        </div>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Invoiced</label>
        <select v-model="filters.client_invoiced" class="h-9 px-2 border border-gray-400 rounded-md w-24">
          <option :value="null">All</option>
          <option :value="true">Yes</option>
          <option :value="false">No</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Paid</label>
        <select v-model="filters.client_paid" class="h-9 px-2 border border-gray-400 rounded-md w-24">
          <option :value="null">All</option>
          <option :value="true">Yes</option>
          <option :value="false">No</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Bank recon.</label>
        <select v-model="filters.bank_reconciled" class="h-9 px-2 border border-gray-400 rounded-md w-24">
          <option :value="null">All</option>
          <option :value="true">Yes</option>
          <option :value="false">No</option>
        </select>
      </div>
      <div class="flex items-center gap-2 pl-2 border-l border-gray-300 self-end h-9">
        <label class="text-xs text-gray-700 flex items-center gap-1 cursor-pointer">
          <input type="checkbox" v-model="filters.include_deleted" />
          Include deleted
        </label>
      </div>
      <button
        v-if="hasActiveFilters"
        class="px-2 py-1.5 text-xs border border-gray-400 rounded hover:bg-gray-100"
        @click="clearFilters"
      >Clear filters</button>
      <base-pagination-header
        :isLoading="loading"
        :page="page"
        :numPages="numPages"
        :page_filter="pageSizeFilter"
        @get-next="getNext"
        @get-prev="getPrev"
        @go-to-first="goToFirst"
        @go-to-last="goToLast"
        @jump-to-page="jumpToPage"
        @set-page-size-filter="setPageSizeFilter"
        @load-data="loadExpenses"
      >
        <template #message>
          <span class="pl-3 text-xs text-gray-600 whitespace-nowrap">
            Page {{ page }} of {{ numPages || 1 }}
          </span>
        </template>
      </base-pagination-header>
      <div class="text-sm text-gray-600 ml-auto self-center">
        Filtered total
        <span class="font-semibold text-dark-primary">${{ formatTotal(filteredTotal) }}</span>
        <span class="text-gray-400 mx-1">·</span>
        {{ expenses.length }} row<span v-if="expenses.length !== 1">s</span>
        <template v-if="selectedIds.length">
          <span class="text-gray-400 mx-1">·</span>
          <span class="font-semibold text-accent-primary">{{ selectedIds.length }} selected</span>
        </template>
        <span v-if="totalCount && totalCount > expenses.length" class="text-gray-400 ml-1">
          (of {{ totalCount }})
        </span>
      </div>
    </div>

    <!-- Bulk action bar -->
    <div
      v-if="selectedIds.length > 0"
      class="flex flex-wrap items-center gap-2 mb-3 p-2 rounded bg-gray-100 border border-gray-300 text-sm"
    >
      <span class="font-semibold">{{ selectedIds.length }} selected</span>
      <button
        class="px-2 py-1 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
        :disabled="notifying || uniqueManagerCount === 0"
        :title="uniqueManagerCount === 0 ? 'None of the selected records have a manager assigned.' : ''"
        @click="notifyManagersClick"
      >{{ notifying ? 'Sending...' : `Notify Manager${uniqueManagerCount === 1 ? '' : 's'} (${uniqueManagerCount})` }}</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="openBulkModal('override_status')"
      >Override Status...</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="openBulkModal('reassign_manager')"
      >Reassign Manager...</button>
      <button
        class="px-2 py-1 rounded border border-red-400 text-red-700 hover:bg-red-50"
        @click="openBulkModal('soft_delete')"
      >Delete...</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="openTagsModal('add')"
      >Add Tag...</button>
      <span class="text-gray-300 mx-1">|</span>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white"
        @click="exportSelectionCsv"
      >Export CSV</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-white disabled:opacity-50"
        :disabled="exportingPdf"
        @click="exportSelectionPdf"
      >{{ exportingPdf ? 'Building PDF...' : 'Export PDF' }}</button>
      <button
        class="ml-auto text-xs text-gray-500 hover:underline"
        @click="selectedIds = []"
      >Clear</button>
    </div>
    </div>

    <!-- Table -->
    <div class="border border-gray-400 rounded-md">
      <table class="table-auto w-full text-sm">
        <thead class="bg-dark-primary text-white sticky z-20" :style="{ top: (topStickyHeight + 56) + 'px' }">
          <tr>
            <th class="px-3 py-2 w-8">
              <input type="checkbox" :checked="allSelected" @change="toggleAllSelected" />
            </th>
            <th class="text-left px-3 py-2 w-16">ID</th>
            <th class="text-left px-3 py-2">Submitter</th>
            <th class="text-left px-3 py-2">Manager</th>
            <th class="text-left px-3 py-2">Date</th>
            <th class="text-left px-3 py-2">Category</th>
            <th class="text-left px-3 py-2">Description</th>
            <th class="text-right px-3 py-2">Amount</th>
            <th class="text-left px-3 py-2">Status</th>
            <th class="text-left px-3 py-2">Client / Engagement</th>
            <th class="text-center px-3 py-2">Receipts</th>
            <th
              class="text-left px-3 py-2 whitespace-nowrap cursor-pointer select-none hover:bg-white/10"
              @click="cycleCreatedSort"
              title="Click to sort by Created"
            >
              Created
              <span v-if="createdSort === 'desc'" class="ml-1">▼</span>
              <span v-else-if="createdSort === 'asc'" class="ml-1">▲</span>
              <span v-else class="ml-1 text-gray-400">▾</span>
            </th>
            <th class="text-right px-3 py-2 w-24">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="13" class="px-3 py-4 text-center text-gray-500">Loading...</td>
          </tr>
          <tr v-else-if="expenses.length === 0">
            <td colspan="13" class="px-3 py-4 text-center text-gray-500">No expenses match these filters.</td>
          </tr>
          <tr
            v-for="exp in expenses"
            :key="exp.id"
            class="border-t border-gray-200 hover:bg-gray-50"
            :class="exp.is_deleted ? 'bg-red-50 text-gray-500 line-through' : ''"
          >
            <td class="px-3 py-2" @click.stop>
              <input type="checkbox" :value="exp.id" v-model="selectedIds" />
            </td>
            <td class="px-3 py-2 whitespace-nowrap text-gray-500">
              <router-link :to="`/expenses/${exp.id}`" class="text-accent-primary hover:underline">
                #{{ exp.id }}
              </router-link>
            </td>
            <td class="px-3 py-2 whitespace-nowrap">
              <span class="group relative inline-block">
                {{ shortUserLabel(exp.submitter) }}
                <span class="pointer-events-none absolute left-0 top-full z-40 mt-1 hidden whitespace-nowrap rounded bg-dark-primary px-1.5 py-0.5 text-xs text-white no-underline shadow-md group-hover:block">
                  {{ userLabel(exp.submitter) }}
                </span>
              </span>
              <div
                v-if="isProxy(exp)"
                class="text-xs text-blue-700"
              >by <span class="group relative inline-block">
                {{ shortUserLabel(exp.created_by) }}
                <span class="pointer-events-none absolute left-0 top-full z-40 mt-1 hidden whitespace-nowrap rounded bg-dark-primary px-1.5 py-0.5 text-xs text-white no-underline shadow-md group-hover:block">
                  {{ userLabel(exp.created_by) }}
                </span>
              </span></div>
            </td>
            <td class="px-3 py-2 whitespace-nowrap">
              <span v-if="exp.manager" class="group relative inline-block">
                {{ shortUserLabel(exp.manager) }}
                <span class="pointer-events-none absolute left-0 top-full z-40 mt-1 hidden whitespace-nowrap rounded bg-dark-primary px-1.5 py-0.5 text-xs text-white no-underline shadow-md group-hover:block">
                  {{ userLabel(exp.manager) }}
                </span>
              </span>
              <span v-else>—</span>
            </td>
            <td class="px-3 py-2 whitespace-nowrap">{{ formatDate(exp.date_of_expense) }}</td>
            <td class="px-3 py-2 whitespace-nowrap">{{ exp.category ? exp.category.name : '—' }}</td>
            <td class="px-3 py-2">
              {{ exp.description }}
              <div v-if="exp.tags && exp.tags.length" class="mt-1 flex flex-wrap gap-1">
                <span
                  v-for="tag in exp.tags"
                  :key="tag.id"
                  @click.stop="addTagToFilter(tag)"
                  title="Click to filter by this tag"
                  class="inline-block px-1.5 py-0.5 text-xs rounded bg-accent-primary text-white cursor-pointer hover:bg-accent-secondary"
                >{{ tag.name }}</span>
              </div>
            </td>
            <td class="px-3 py-2 text-right whitespace-nowrap">
              <span
                v-if="exp.payment_method === 'company_paid'"
                class="mr-1 px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700"
                title="Paid via company card"
              >co.</span>
              ${{ formatAmount(exp.amount) }}
            </td>
            <td class="px-3 py-2">
              <StatusPill :status="exp.status" />
              <div v-if="exp.is_retro" class="mt-1">
                <span class="inline-block px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">Retro</span>
              </div>
            </td>
            <td class="px-3 py-2">
              <template v-if="exp.is_client_reimbursable">
                <span>{{ exp.client_name || '—' }}</span>
                <span v-if="exp.engagement_title"> — {{ exp.engagement_title }}</span>
              </template>
              <span v-else class="text-gray-400">—</span>
            </td>
            <td class="px-3 py-2 text-center">{{ exp.receipt_count || 0 }}</td>
            <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{{ formatDateTime(exp.created_at) }}</td>
            <td class="px-3 py-2 text-right whitespace-nowrap">
              <div class="relative inline-block">
                <button
                  class="px-2 py-1 text-xs border border-gray-400 rounded hover:bg-gray-100"
                  @click.stop="toggleMenu(exp.id)"
                >Actions ▾</button>
                <div
                  v-if="openMenuId === exp.id"
                  v-click-outside="() => closeMenu(exp.id)"
                  class="absolute right-0 mt-1 z-20 w-52 bg-white border border-gray-300 rounded shadow-md text-left text-xs"
                >
                  <button
                    class="block w-full text-left px-3 py-1.5 hover:bg-gray-100"
                    @click="openModal(exp, 'override_status')"
                  >Override status...</button>
                  <button
                    class="block w-full text-left px-3 py-1.5 hover:bg-gray-100"
                    @click="openModal(exp, 'reassign_manager')"
                  >Reassign manager...</button>
                  <router-link
                    :to="`/expenses/${exp.id}`"
                    class="block w-full text-left px-3 py-1.5 hover:bg-gray-100"
                  >Open detail page</router-link>
                  <div class="border-t border-gray-200 my-1"></div>
                  <button
                    v-if="!exp.is_deleted"
                    class="block w-full text-left px-3 py-1.5 text-red-700 hover:bg-red-50"
                    @click="openModal(exp, 'soft_delete')"
                  >Delete...</button>
                  <button
                    v-else
                    class="block w-full text-left px-3 py-1.5 text-green-700 hover:bg-green-50"
                    @click="openModal(exp, 'restore')"
                  >Restore...</button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <AdminReasonModal
      :open="modalOpen"
      :mode="modalMode"
      :expense="modalExpense"
      :users="users"
      @close="modalOpen = false"
      @applied="onAdminApplied"
    />

    <AdminBulkActionModal
      :open="bulkModalOpen"
      :mode="bulkModalMode"
      :expense-ids="selectedIds"
      :users="users"
      @close="bulkModalOpen = false"
      @applied="onBulkApplied"
    />

    <TagsBulkModal
      v-if="tagsModalOpen"
      :recordIds="selectedIds"
      :mode="tagsModalMode"
      @close="tagsModalOpen = false"
      @applied="onTagsApplied"
    />
  </div>
</template>

<script>
export default { name: "all-expenses-admin" };
</script>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import { getExpenses, getExpensePickerEngagements, notifyManagers } from "@/services/Expense.js";
import { getExpenseCategories } from "@/services/ExpenseCategory.js";
import { getAllUsers } from "@/services/User.js";
import StatusPill from "@/components/expenses/StatusPill.vue";
import TagPicker from "@/components/expenses/TagPicker.vue";
import AdminReasonModal from "./AdminReasonModal.vue";
import AdminBulkActionModal from "./AdminBulkActionModal.vue";
import TagsBulkModal from "@/components/expenses/TagsBulkModal.vue";
import { useStickyOffset } from "@/composables/useStickyOffset";

const store = useStore();
const $toast = useToast();

const loading = ref(false);
const expenses = ref([]);
const totalCount = ref(0);
const page = ref(1);
const numPages = ref(1);
const pageSizeFilter = reactive({ pageSizeFilter: 100 });
const users = ref([]);
const categories = ref([]);
const engagements = ref([]);

const openMenuId = ref(null);
const modalOpen = ref(false);
const modalMode = ref('override_status');
const modalExpense = ref(null);
const selectedIds = ref([]);
const topStickyRef = ref(null);
const topStickyHeight = useStickyOffset(topStickyRef);
const notifying = ref(false);
const bulkModalOpen = ref(false);
const bulkModalMode = ref('override_status');
const exportingPdf = ref(false);
const tagsModalOpen = ref(false);
const tagsModalMode = ref('add');

function openBulkModal(mode) {
  if (selectedIds.value.length === 0) return;
  bulkModalMode.value = mode;
  bulkModalOpen.value = true;
}

function openTagsModal(mode) {
  if (selectedIds.value.length === 0) return;
  tagsModalMode.value = mode || 'add';
  tagsModalOpen.value = true;
}

function onTagsApplied() {
  tagsModalOpen.value = false;
  selectedIds.value = [];
  loadExpenses();
}

function onBulkApplied(result) {
  const { total, succeeded, failed } = result;
  $toast.open({
    message: failed
      ? `${succeeded} of ${total} applied; ${failed} failed.`
      : `${succeeded} record${succeeded === 1 ? '' : 's'} updated.`,
    type: failed ? 'warning' : 'success',
    duration: 3500,
  });
  if (succeeded > 0) {
    selectedIds.value = [];
    loadExpenses();
  }
}

function selectionRows() {
  return expenses.value.filter((e) => selectedIds.value.includes(e.id));
}

function csvCell(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportSelectionCsv() {
  const rows = selectionRows();
  if (rows.length === 0) return;
  const headers = [
    'ID', 'Submitter', 'Submitter Username', 'Manager', 'Created By',
    'Date', 'Amount', 'Category', 'Description', 'Status',
    'Group Label', 'Client', 'Engagement', 'Payment Method',
    'Reimbursed', 'Invoiced', 'Client Paid', 'Bank Reconciled',
    'Payroll Run', 'Invoice Ref', 'Receipts', 'Updated At', 'Is Deleted',
  ];
  const lines = [headers.map(csvCell).join(',')];
  for (const e of rows) {
    lines.push([
      e.id,
      userLabel(e.submitter),
      e.submitter ? e.submitter.username : '',
      e.manager ? userLabel(e.manager) : '',
      e.created_by ? userLabel(e.created_by) : '',
      e.date_of_expense || '',
      e.amount,
      e.category ? e.category.name : '',
      e.description,
      e.status,
      e.group_label || '',
      e.is_client_reimbursable ? (e.client_name || '') : '',
      e.is_client_reimbursable ? (e.engagement_title || '') : '',
      e.payment_method,
      e.submitter_reimbursed ? 'Yes' : 'No',
      e.client_invoiced ? 'Yes' : 'No',
      e.client_paid ? 'Yes' : 'No',
      e.bank_reconciled ? 'Yes' : 'No',
      e.payroll_batch_reference || '',
      e.client_invoice_batch_reference || '',
      e.receipt_count || 0,
      e.updated_at || '',
      e.is_deleted ? 'Yes' : 'No',
    ].map(csvCell).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `aerocle-expenses-${stamp}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

async function exportSelectionPdf() {
  const rows = selectionRows();
  if (rows.length === 0) return;
  exportingPdf.value = true;
  try {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    const generatedAt = new Date().toLocaleString();
    const total = rows.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    doc.setFontSize(14);
    doc.text('Aerocle Expenses — Admin Selection', 40, 36);
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`Generated ${generatedAt} · ${rows.length} record(s)`, 40, 50);
    doc.setTextColor(0);
    autoTable(doc, {
      startY: 64,
      head: [[
        'ID', 'Submitter', 'Manager', 'Date', 'Category',
        'Description', 'Amount', 'Status', 'Client / Engagement',
        'Reimb.', 'Inv.', 'Paid', 'Recon.',
      ]],
      body: rows.map((e) => [
        `#${e.id}`,
        userLabel(e.submitter),
        e.manager ? userLabel(e.manager) : '—',
        e.date_of_expense || '',
        e.category ? e.category.name : '—',
        e.description,
        `$${formatAmount(e.amount)}`,
        e.status,
        e.is_client_reimbursable
          ? `${e.client_name || '—'}${e.engagement_title ? ' — ' + e.engagement_title : ''}`
          : '—',
        e.submitter_reimbursed ? 'Yes' : 'No',
        e.client_invoiced ? 'Yes' : 'No',
        e.client_paid ? 'Yes' : 'No',
        e.bank_reconciled ? 'Yes' : 'No',
      ]),
      foot: [[
        { content: 'Total', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: `$${formatTotal(total)}`, styles: { fontStyle: 'bold' } },
        '', '', '', '', '', '',
      ]],
      styles: { fontSize: 7.5, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: [37, 39, 77], textColor: 255 },
      footStyles: { fillColor: [240, 240, 240], textColor: 0 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 70 },
        2: { cellWidth: 70 },
        3: { cellWidth: 50 },
        4: { cellWidth: 60 },
        6: { cellWidth: 45, halign: 'right' },
        7: { cellWidth: 60 },
        9: { cellWidth: 35, halign: 'center' },
        10: { cellWidth: 30, halign: 'center' },
        11: { cellWidth: 30, halign: 'center' },
        12: { cellWidth: 35, halign: 'center' },
      },
      margin: { left: 40, right: 40 },
    });
    const stamp = new Date().toISOString().slice(0, 10);
    doc.save(`aerocle-expenses-${stamp}.pdf`);
  } catch (e) {
    $toast.open({ message: 'Failed to build PDF.', type: 'error', duration: 3000 });
  }
  exportingPdf.value = false;
}

const selectedRecords = computed(() =>
  expenses.value.filter((e) => selectedIds.value.includes(e.id))
);
const uniqueManagerCount = computed(() => {
  const ids = new Set();
  for (const e of selectedRecords.value) {
    if (e.manager && e.manager.id) ids.add(e.manager.id);
  }
  return ids.size;
});
const allSelected = computed(() =>
  expenses.value.length > 0 &&
  expenses.value.every((e) => selectedIds.value.includes(e.id))
);

function toggleAllSelected() {
  if (allSelected.value) {
    selectedIds.value = [];
  } else {
    selectedIds.value = expenses.value.map((e) => e.id);
  }
}

async function notifyManagersClick() {
  if (selectedIds.value.length === 0 || uniqueManagerCount.value === 0) return;
  notifying.value = true;
  const token = store.state.auth.token;
  try {
    const result = await notifyManagers(token, [...selectedIds.value]);
    const lines = (result.notified || [])
      .map((m) => `${m.manager_name}: ${m.count}`)
      .join(', ');
    const msg = `Notified ${result.total_managers} manager(s)`
      + (lines ? ` — ${lines}` : '')
      + (result.records_without_manager
          ? ` · ${result.records_without_manager} selected record(s) had no manager and were skipped.`
          : '');
    $toast.open({ message: msg, type: 'success', duration: 4000 });
    selectedIds.value = [];
  } catch (e) {
    $toast.open({
      message: e?.response?.data?.detail || 'Failed to send notifications.',
      type: 'error', duration: 3000,
    });
  }
  notifying.value = false;
}

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted — Pending Manager' },
  { value: 'manager_approved', label: 'Manager Approved — Pending Acctg' },
  { value: 'accounting_approved', label: 'Accounting Approved' },
  { value: 'closed', label: 'Closed' },
  { value: 'rejected', label: 'Rejected' },
];

const filters = reactive({
  q: '',
  status: null,
  submitter: null,
  manager: null,
  created_by: null,
  category: null,
  engagement: null,
  payment_method: null,
  is_client_reimbursable: null,
  is_retro: null,
  group_label__icontains: '',
  tags: [],
  tag_combinator: 'or',
  exclude_tags: [],
  exclude_tag_combinator: 'or',
  date_of_expense__gte: null,
  date_of_expense__lte: null,
  submitter_reimbursed: null,
  client_invoiced: null,
  client_paid: null,
  bank_reconciled: null,
  include_deleted: false,
});

const hasActiveFilters = computed(() => {
  for (const [k, v] of Object.entries(filters)) {
    if (k === 'include_deleted') {
      if (v) return true;
      continue;
    }
    if (k === 'tag_combinator') continue;
    if (Array.isArray(v)) { if (v.length) return true; continue; }
    if (v !== null && v !== '' && v !== false) return true;
  }
  return false;
});

function clearFilters() {
  filters.q = '';
  filters.status = null;
  filters.submitter = null;
  filters.manager = null;
  filters.created_by = null;
  filters.category = null;
  filters.engagement = null;
  filters.payment_method = null;
  filters.is_client_reimbursable = null;
  filters.is_retro = null;
  filters.group_label__icontains = '';
  filters.tags = [];
  filters.tag_combinator = 'or';
  filters.exclude_tags = [];
  filters.exclude_tag_combinator = 'or';
  filters.date_of_expense__gte = null;
  filters.date_of_expense__lte = null;
  filters.submitter_reimbursed = null;
  filters.client_invoiced = null;
  filters.client_paid = null;
  filters.bank_reconciled = null;
  filters.include_deleted = false;
}

function buildQueryString() {
  const parts = [
    `page_size=${pageSizeFilter.pageSizeFilter || 100}`,
    `page=${page.value}`,
  ];
  for (const [k, v] of Object.entries(filters)) {
    if (k === 'tags' || k === 'tag_combinator' || k === 'exclude_tags' || k === 'exclude_tag_combinator') continue;
    if (v === null || v === '' || v === undefined) continue;
    parts.push(`${k}=${encodeURIComponent(v)}`);
  }
  if (filters.tags && filters.tags.length) {
    for (const tag of filters.tags) {
      const value = tag.id ?? tag.name;
      if (value != null && value !== '') parts.push(`tag=${encodeURIComponent(value)}`);
    }
    if (filters.tag_combinator && filters.tag_combinator !== 'or') {
      parts.push(`tag_combinator=${encodeURIComponent(filters.tag_combinator)}`);
    }
  }
  if (filters.exclude_tags && filters.exclude_tags.length) {
    for (const tag of filters.exclude_tags) {
      const value = tag.id ?? tag.name;
      if (value != null && value !== '') parts.push(`exclude_tag=${encodeURIComponent(value)}`);
    }
    if (filters.exclude_tag_combinator && filters.exclude_tag_combinator !== 'or') {
      parts.push(`exclude_tag_combinator=${encodeURIComponent(filters.exclude_tag_combinator)}`);
    }
  }
  if (createdSort.value) {
    parts.push(`ordering=${createdSort.value === 'desc' ? '-' : ''}created_at`);
  }
  return `?${parts.join('&')}`;
}

const createdSort = ref(null); // null | 'asc' | 'desc'
function cycleCreatedSort() {
  createdSort.value = createdSort.value === null ? 'desc'
    : createdSort.value === 'desc' ? 'asc'
    : null;
  page.value = 1;
  scheduleReload();
}

function getNext() { if (page.value < numPages.value) { page.value += 1; loadExpenses(); } }
function getPrev() { if (page.value > 1) { page.value -= 1; loadExpenses(); } }
function goToFirst() { page.value = 1; loadExpenses(); }
function goToLast() { page.value = numPages.value || 1; loadExpenses(); }
function jumpToPage(target) {
  const n = Number(target);
  if (!n || n < 1 || n > (numPages.value || 1)) return;
  page.value = n;
  loadExpenses();
}
function setPageSizeFilter(target) {
  if (!target) return;
  pageSizeFilter.pageSizeFilter = target === 'clear' ? 100 : Number(target);
  page.value = 1;
  loadExpenses();
}

let reloadTimer = null;
function scheduleReload() {
  if (reloadTimer) clearTimeout(reloadTimer);
  reloadTimer = setTimeout(loadExpenses, 250);
}

watch(filters, () => { page.value = 1; scheduleReload(); }, { deep: true });

async function loadExpenses() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    const data = await getExpenses(token, buildQueryString());
    if (Array.isArray(data)) {
      expenses.value = data;
      totalCount.value = data.length;
      numPages.value = 1;
      page.value = 1;
    } else {
      expenses.value = data.results || [];
      totalCount.value = data.count || expenses.value.length;
      numPages.value = data.num_pages || 1;
      // Honor whatever the backend says the current page is, in case our
      // local page number got clipped (e.g., page 7 after a filter change).
      if (typeof data.page === 'number') page.value = data.page;
    }
  } catch (e) {
    $toast.open({ message: 'Failed to load expenses', type: 'error', duration: 3000 });
    expenses.value = [];
    totalCount.value = 0;
    numPages.value = 1;
  }
  loading.value = false;
}

async function loadDictionaries() {
  const token = store.state.auth.token;
  try {
    const [u, c, e] = await Promise.all([
      getAllUsers(token),
      getExpenseCategories(token),
      getExpensePickerEngagements(token),
    ]);
    // Restrict picker to active users that belong to at least one
    // group — strict equality so a backend that doesn't yet return
    // these fields falls back to empty rather than silently showing
    // every account (test users, deactivated, no-group placeholders).
    users.value = (u && u.users ? u.users : [])
      .filter((x) => x && x.id && x.is_active === true && x.has_groups === true)
      .sort((a, b) => userLabel(a).toLowerCase().localeCompare(userLabel(b).toLowerCase()));
    categories.value = Array.isArray(c) ? c : (c?.results || []);
    engagements.value = Array.isArray(e) ? e : (e?.results || []);
  } catch (err) {
    // Pickers will just be empty — page still works.
  }
}

const filteredTotal = computed(() => {
  return expenses.value.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
});

function userLabel(u) {
  if (!u) return '—';
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return name || u.username || `User #${u.id}`;
}

// Abbreviate names (i.e "Gordon W") to keep the row
// from overflowing the table
function shortUserLabel(u) {
  if (!u) return '—';
  const first = (u.first_name || '').trim();
  const last = (u.last_name || '').trim();
  if (first && last) return `${first} ${last[0]}`;
  return userLabel(u);
}

// Used in the dropdowns so two active accounts with the same display
// name (e.g. multiple "Norman Chui" rows) can be distinguished by
// username at a glance.
function pickerLabel(u) {
  if (!u) return '—';
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  if (name && u.username) return `${name} (${u.username})`;
  return name || u.username || `User #${u.id}`;
}

function isProxy(exp) {
  return exp && exp.created_by && exp.submitter && exp.created_by.id !== exp.submitter.id;
}

function formatDate(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  return d.toLocaleDateString();
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function addTagToFilter(tag) {
  if (!tag) return;
  const idMatch = tag.id != null && filters.tags.some((t) => t.id === tag.id);
  const nameMatch = filters.tags.some((t) => (t.name || '').toLowerCase() === (tag.name || '').toLowerCase());
  if (idMatch || nameMatch) return;
  filters.tags.push({ id: tag.id, name: tag.name });
}

function formatAmount(a) {
  if (a === null || a === undefined) return '0.00';
  const n = typeof a === 'string' ? parseFloat(a) : a;
  return n.toFixed(2);
}

function formatTotal(n) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function toggleMenu(id) {
  openMenuId.value = openMenuId.value === id ? null : id;
}

function closeMenu(id) {
  if (openMenuId.value === id) openMenuId.value = null;
}

function openModal(exp, mode) {
  modalExpense.value = exp;
  modalMode.value = mode;
  modalOpen.value = true;
  openMenuId.value = null;
}

function onAdminApplied(updated) {
  if (!updated) return;
  $toast.open({ message: `Expense #${updated.id} updated.`, type: 'success', duration: 2000 });
  // Refetch so soft-delete/restore visibility updates against include_deleted.
  loadExpenses();
}

onMounted(async () => {
  await loadDictionaries();
  await loadExpenses();
});
</script>